/**
 * Resolve a snake_case enum value to a translated display label.
 *
 * Code keeps values in English snake_case (`'life_threatening'`). UI labels
 * live under camelCase keys (`patient:allergy.severity.lifeThreatening`).
 * This helper is the one place that knows the transform.
 *
 * Returns the raw value if the key is missing. The drift gate is the real
 * invariant against missing keys; the fallback is defensive.
 *
 * @param t        i18next translation function; structurally typed so that
 *                 narrowed `TFunction<'ns'>` shapes from `useTranslation('ns')`
 *                 assign cleanly without a cast at the call site.
 * @param namespace  i18next namespace, e.g. `'patient'`.
 * @param group    Dot-path within the namespace, e.g. `'allergy.severity'`.
 * @param value    English snake_case value (lowercase). Uppercase or
 *                 already-camel values fall through the transform unchanged.
 */
const MISSING = '__ENUM_LABEL_MISSING__';

/**
 * Pure snake_case → camelCase transform. Exported for direct unit testing
 * (the regex relies on the `/g` flag to catch every underscore, not just the
 * first; a separate unit lets the test prove that without round-tripping
 * through i18next).
 */
export function snakeToCamel(value: string): string {
  return value.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

type TFunctionLike = (
  key: string,
  options?: { defaultValue?: string },
) => string;

export function enumLabel(
  t: TFunctionLike,
  namespace: string,
  group: string,
  value: string,
  fallback?: string,
): string {
  const key = `${namespace}:${group}.${snakeToCamel(value)}`;
  // i18next's missing-key return strips the namespace, so an equality check
  // against `key` is unreliable. A sentinel defaultValue is unambiguous.
  const resolved = t(key, { defaultValue: MISSING });
  if (resolved !== MISSING) return String(resolved);
  // Server-supplied English display name, when the caller has one. Vocabularies
  // that live in the database (the `providertype` table) can gain an entry
  // after a release, and no locale file will know its key — without this the
  // picker would show a raw `snake_case` wire value to a clinician.
  return fallback ?? value;
}
